// API response shapes — returned by the backend, consumed by the frontend

export interface User {
  id: string;
  email: string;
  fullName: string;
  phone: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface Location {
  id: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  city: string;
  address: string;
  defaultCapacity: number;
}

export const CALL_IN_REQ_STATUSES = [
  'DUE',
  'CONTACT_ATTEMPT_1',
  'CONTACT_ATTEMPT_2',
  'CONTACT_ATTEMPT_3',
  'IN_PROGRESS',
  'COMPLETED',
] as const;
export type CallInReqStatus = (typeof CALL_IN_REQ_STATUSES)[number];

export interface CallInReq {
  id: number;
  employeeId: string;
  startDate: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  status: CallInReqStatus;
  lastReminderDate: string | null;
}

export const APPOINTMENT_STATUSES = ['BOOKED', 'COMPLETED', 'ABSENT', 'CANCELLED'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export const DEVICE_TYPES = [
  'power_supply',
  'mobile_charger',
  'notebook_laptop',
  'display',
  'printer_plotter',
  'scanners',
  'other',
] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

export interface DeviceTypeCount {
  type: DeviceType;
  count: number;
}

export const DEVICE_CHECK_RESULTS = ['PASSED', 'FAILED'] as const;
export type DeviceCheckResult = (typeof DEVICE_CHECK_RESULTS)[number];

export interface AppointmentDevice {
  id: number;
  name: string;
  type: DeviceType;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface AppointmentDeviceCheck {
  id: number;
  appointmentId: number;
  deviceId: number;
  comment: string | null;
  result: DeviceCheckResult;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CallInReqDetail {
  id: number;
  employeeName: string;
  employeeEmail: string;
  employeePhone: string;
  status: CallInReqStatus;
  lastReminderDate: string | null;
  linkedAppointments: { id: number; status: AppointmentStatus }[];
}

export interface CallInReqViewList {
  results: Array<{
    id: number;
    employeeName: string;
    employeeEmail: string;
    status: CallInReqStatus;
    lastReminderDate: string | null;
    linkedAppointments: string[];
  }>;
  rowCount: number;
}

export interface ServiceScheduleDeviceResult {
  id: number;
  name: string;
  type: DeviceType;
  result: DeviceCheckResult;
  comment: string | null;
}

export interface ServiceScheduleDetail {
  id: number;
  status: AppointmentStatus;
  startDateTime: string;
  endDateTime: string;
  locationId: number;
  city: string;
  address: string;
  deviceTypes: DeviceTypeCount[];
  employeeName: string;
  employeeEmail: string;
  employeePhone?: string;
  devices?: ServiceScheduleDeviceResult[];
}

export interface ServiceScheduleDesk {
  deskId: number;
  deskName: string;
  appointments: ServiceScheduleDeskAppointment[];
}

export interface ServiceScheduleDeskAppointment {
  id: number;
  status: AppointmentStatus;
  startDateTime: string;
  endDateTime: string;
  employeeName: string;
}

export interface Appointment {
  id: number;
  locationId: number;
  callInReqId: number;
  startDateTime: string;
  endDateTime: string;
  deviceTypes: DeviceTypeCount[];
  deskNumber: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  status: AppointmentStatus;
}

export interface MyAppointment {
  id: number;
  locationId: number;
  callInReqId: number;
  startDateTime: string;
  endDateTime: string;
  deviceTypes: DeviceTypeCount[];
  deskNumber: number;
  status: AppointmentStatus;
  location: {
    city: string;
    address: string;
  };
}

export interface LocationCapacityOverride {
  id: number;
  locationId: number;
  date: string;
  capacity: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export type BannerSeverity = 'error' | 'success' | 'info' | 'warn' | 'secondary' | 'contrast';

export interface ApiBanner {
  translationKey: string;
  translationParams?: Record<string, unknown>;
  severity?: BannerSeverity;
  icon?: string;
  iconColor?: string;
  closable?: boolean;
}

type Action = 'C' | 'R' | 'U' | 'D'; // Create | Read | Update | Delete
type Scope = 'A' | 'O'; // All | Own

type Crud<T extends string> = `${Action}${Scope}.${T}`;

export type UserPermissions =
  | 'RA.Locations'
  | 'RA.LocationSlots'
  | Crud<'Appointments'>
  | Crud<'CallInReqs'>
  | 'UI.TechnicianServiceSchedule'
  | 'UI.TechnicianCallInReqs'
  | 'UI.EmployeeAppointments';
